import { RemoteShellView } from '../view/RemoteShellView';
import { templateRemoteShell } from '../view/template';
import { RemoteShellRepository } from '../repository/RemoteShellRepository';
import { BaseWebComponentController } from '../../../framework/BaseWebComponentController';
import { RemoteShellWebComponent } from '..';
import {
  OPERATING_SYSTEM_WINDOWS,
  OPERATING_SYSTEM_LINUX,
  RemoteShellWebComponentSchema,
  PREPARING_DOWNLOAD,
  RemoteShellErrorCodes,
} from '../types';
import { ElementClass, elementClassListAdd, elementClassListRemove } from '../../../utils/htmlElement.utils';
import {
  CONNECTION_ERROR,
  CONNECTION_FAILED,
  START_CONNECTION,
  CONNECTION_SUCCESSFUL,
  CONNECTION_INACTIVE,
  CONNECTION_ENDED,
} from '../../terminal/types';
import { GzToast, ToastActionButtonMode, ToastSeverity } from '../../../ui/gzToastNotification/type';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import { DATA_LOADED_EVENT } from '../../../endpointDetails/types';
import { ButtonEvents } from '../../../ui/gzButton/types';
import { GzDialog } from '../../../ui/gzDialog/component/GzDialog';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { UploadWindowWebComponent } from '../../uploadWindow';
import { FILE_ADDED, FILE_REMOVED, START_VALIDATE_PATH, VALIDATE_PATH } from '../../uploadWindow/types';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { translateFn } from '../../../utils/translator.helper';
import { ConsoleRequest, DataProviderType } from '../../../utils/request';
import { CompanyEndpointMethod } from '../../../quarantine/exchangeProtection/types';

export class RemoteShellController extends BaseWebComponentController {
  public component: RemoteShellWebComponent;
  public dataRepository: RemoteShellRepository;
  public view: RemoteShellView;
  public viewConstructor: typeof RemoteShellView;
  public dataRepositoryConstructor: typeof RemoteShellRepository;

  protected _streamId: string | null = null;
  private _maxFilesSize: number;

  constructor(component: RemoteShellWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: RemoteShellView,
      viewTemplate: templateRemoteShell,
      dataRepositoryConstructor: RemoteShellRepository,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$terminalContainer,
        event: START_CONNECTION,
        callback: this.handleConnectionStarted,
      },
      {
        element: this.view.dom.$terminalContainer,
        event: CONNECTION_SUCCESSFUL,
        callback: this.handleSuccessfulConnection,
      },
      {
        element: this.view.dom.$terminalContainer,
        event: CONNECTION_FAILED,
        callback: this.handleFailedConnection,
      },
      {
        element: this.view.dom.$terminalContainer,
        event: CONNECTION_ERROR,
        callback: this.handleFailedConnection,
      },
      {
        element: this.view.dom.$terminalContainer,
        event: CONNECTION_INACTIVE,
        callback: this.handleConnectionInactive,
      },
      {
        element: this.view.dom.$terminalContainer,
        event: CONNECTION_ENDED,
        callback: this.handleConnectionEnded,
      },
      {
        element: this.view.dom.$endSessionButton,
        event: ButtonEvents.CLICK,
        callback: this.openEndSessionDialog,
      },
      {
        element: this.view.dom.$endpointDetails,
        event: DATA_LOADED_EVENT,
        callback: this.setDataOnComponent,
      },
      {
        element: this.view.dom.$uploadButton,
        event: 'click',
        callback: this.handleUploadButtonClick,
      },
      {
        element: this.view.dom.$downloadButton,
        event: 'click',
        callback: this.handleDownloadButtonClick,
      },
      {
        element: this.view.dom.$wrapper,
        event: PREPARING_DOWNLOAD,
        callback: this.handlePreparingDownloadProcess,
      },
    ];
  }

  async getDataForView(): Promise<RemoteShellWebComponentSchema> {
    return {
      hasError: this.component.hasError,
    };
  }

  protected handleSuccessfulConnection(event): void {
    elementClassListAdd(this.view.dom.$loadingOverlay, ElementClass.HIDDEN);
    elementClassListRemove(this.view.dom.$leftCardButtonsBox, ElementClass.HIDDEN);
    elementClassListRemove(this.view.dom.$connectionDetails, ElementClass.HIDDEN);

    this._streamId = event.detail.streamId;
  }

  protected handleFailedConnection(event): void {
    elementClassListAdd(this.view.dom.$loadingOverlay, ElementClass.HIDDEN);
    elementClassListAdd(this.view.dom.$leftCardButtonsBox, ElementClass.HIDDEN);

    const { error } = event.detail;
    if (error) {
      this.createErrorToast(error);
    }
    this._streamId = null;
  }

  protected handleSuccessfulDisconnect(): void {
    elementClassListAdd(this.view.dom.$leftCardButtonsBox, ElementClass.HIDDEN);
    elementClassListAdd(this.view.dom.$loadingOverlay, ElementClass.HIDDEN);

    this._streamId = null;
  }

  protected handleConnectionStarted(): void {
    elementClassListRemove(this.view.dom.$loadingOverlay, ElementClass.HIDDEN);
  }

  protected handleConnectionInactive(): void {
    const translations = this.view.getTranslations();
    const toastNotificationConfigs: GzToast = {
      message: translations.inactivityTimeoutText,
      hasAction: false,
      severity: ToastSeverity.WARNING,
      keepOpen: true,
    };

    GzToastNotificationWebComponent.buildToastWrapperElement(toastNotificationConfigs, undefined, false);
  }

  protected handleConnectionEnded(): void {
    elementClassListAdd(this.view.dom.$leftCardButtonsBox, ElementClass.HIDDEN);
    elementClassListAdd(this.view.dom.$connectionDetails, ElementClass.HIDDEN);

    this.view.closeDialog();
    GzToastNotificationWebComponent.closeAllToasts();
    this._streamId = null;
  }

  protected async setDataOnComponent(event): Promise<void> {
    const { endpoint } = event.detail;
    this.view.dom.$terminalContainer.authenticationSettings = await this.dataRepository.getAuthenticationSettings(
      this.dependencies.user.companyId
    );
    this.view.dom.$terminalContainer.endpoint = endpoint;

    await this.setMaxFilesSize(endpoint.companyId)
    this.view.dom.$terminalContainer.maxFilesSize = this._maxFilesSize;
  }

  private async setMaxFilesSize(companyId: string) {
    this._maxFilesSize = await ConsoleRequest.createRequest<any>({
      dataProviderType: DataProviderType.CORE,
      endpoint: 'Company',
      method: 'getCompanyCentralizedStorageLimit',
      payload: {
        companyId: companyId,
        profile: 'remoteAccess',
      },
    });
  }

  protected createErrorToast(error) {
    const { code } = error;

    const toastNotificationConfigs: GzToast = {
      message: this.view.translateNirvanaErrorCodes(code),
      hasAction: false,
      severity: ToastSeverity.ERROR,
      keepOpen: true,
    };

    GzToastNotificationWebComponent.buildToastWrapperElement(toastNotificationConfigs, undefined, false);
  }

  protected openEndSessionDialog() {
    const translations = this.view.getTranslations();

    const dialogBody = document.createElement('div');
    dialogBody.innerHTML = `<gz-text fontSize="15">${translations.endSessionDialog.body}</gz-text>`;

    const dialogFooter = document.createElement('div');
    dialogFooter.innerHTML = `
        <gz-button id="confirm" class="mr-2">${translations.endSessionDialog.confirmButtonText}</gz-button>
        <gz-button id="cancel" type="outline" color="secondary">${translations.endSessionDialog.cancelButtonText}</gz-button>
    `;

    const dialogConfigs: GzDialog = {
      headline: translations.endSessionDialog.headline,
      bodyContent: dialogBody,
      footerContent: dialogFooter,
      isOpen: true,
    };

    const dialog = GzDialogWebComponent.buildDialogElement(dialogConfigs);

    const footerCancelBtn = dialogFooter.querySelector('#cancel');
    const footerConfirmBtn = dialogFooter.querySelector('#confirm');

    footerCancelBtn?.addEventListener('click', () => {
      dialog.dismiss();
    });

    footerConfirmBtn?.addEventListener('click', () => {
      dialog.dismiss();
      this.view.dom.$terminalContainer.handleDisconnect();
      this.handleSuccessfulDisconnect();
    });
  }

  async handleUploadButtonClick() {
    GzToastNotificationWebComponent.closeAllToasts();
    this.view.dom.$uploadButton.showSpinner = true;

    const response = await this.getCurrentPath();
    this.view.dom.$uploadButton.showSpinner = false;

    const dialogFooter = document.createElement('div');
    dialogFooter.innerHTML = `
        <gz-button id="confirm" class="mr-2" disabled>${translateFn('Upload')}</gz-button>
        <gz-button id="cancel" type="outline" color="secondary">${translateFn('Cancel')}</gz-button>
      `;

    const uploadComponent = this.initializeUploadComponent(
      response?.path ?? '',
      dialogFooter.querySelector('#confirm') as GzButtonWebComponent
    );

    this.initializeDialog(dialogFooter, uploadComponent);

    uploadComponent.injectDependencies(this.dependencies);
    await uploadComponent.start();
    await uploadComponent.validate();
  }

  async handleDownloadButtonClick() {
    GzToastNotificationWebComponent.closeAllToasts();
    this.view.dom.$downloadButton.showSpinner = true;

    const response = await this.getCurrentPath();
    this.view.dom.$downloadButton.showSpinner = false;

    await this.view.handleDownloadButtonClick(response?.path ?? '', this._streamId, this._maxFilesSize);
  }

  initializeUploadComponent(path, dialogConfirmButton) {
    const uploadComponent = new UploadWindowWebComponent();
    uploadComponent.streamId = this._streamId;
    uploadComponent.path = path;
    uploadComponent.maxFilesSize = this._maxFilesSize;

    uploadComponent.addEventListener(START_VALIDATE_PATH, () => {
      dialogConfirmButton.disabled = true;
      dialogConfirmButton.showSpinner = true;
    });

    uploadComponent.addEventListener(VALIDATE_PATH, () => {
      dialogConfirmButton.disabled = !uploadComponent.isValid();
      dialogConfirmButton.showSpinner = false;
    });

    uploadComponent.addEventListener(FILE_ADDED, () => {
      if (dialogConfirmButton.showSpinner) {
        return;
      }

      dialogConfirmButton.disabled = !uploadComponent.isValid();
    });

    uploadComponent.addEventListener(FILE_REMOVED, () => {
      if (dialogConfirmButton.showSpinner) {
        return;
      }

      dialogConfirmButton.disabled = !uploadComponent.isValid();
    });

    return uploadComponent;
  }

  initializeDialog(dialogFooter, uploadComponent) {
    const dialogConfig = {
      headline: this.dependencies.translateFn('Upload files'),
      bodyContent: uploadComponent,
      isOpen: true,
      isWindow: true,
      closableOutsideBox: false,
      footerContent: dialogFooter,
    };

    const footerConfirmBtn = dialogFooter.querySelector('#confirm') as GzButtonWebComponent;
    footerConfirmBtn?.addEventListener('click', async () => {
      if (footerConfirmBtn.showSpinner) {
        return;
      }

      // intentionally left without await because we want to show notification while request is running
      this.uploadFiles(uploadComponent);
      GzToastNotificationWebComponent.buildToastWrapperElement(
        {
          message: translateFn('Upload started. Do not end the remote session.'),
          severity: ToastSeverity.INFORMATIVE,
        },
        undefined,
        false
      );
      this.view.closeDialog();
    });

    const footerCancelBtn = dialogFooter.querySelector('#cancel');
    footerCancelBtn?.addEventListener('click', () => {
      if (footerConfirmBtn.showSpinner) {
        return;
      }

      this.view.closeDialog();
    });

    this.view.dialog = GzDialogWebComponent.buildDialogElement(dialogConfig, this.view.dom.$wrapper);
  }

  async getCurrentPath() {
    // // this function is not available on Windows, so no need to call it
    if (this.view.dom.$terminalContainer.endpoint.operatingSystem === OPERATING_SYSTEM_WINDOWS) {
      return null;
    }

    try {
      return this.dependencies.api.clients.RemoteShell.makeRequest('gzConsoleRemoteShell/getCurrentPath', {
        streamId: this._streamId,
      }).toPromise();
    } catch (err) {
      console.log(err);
      const toastNotificationConfigs: GzToast = {
        message: translateFn('Internal server error: {ERROR_CODE}. Contact Bitdefender Enterprise Support.', {
          ERROR_CODE: RemoteShellErrorCodes.INTERNAL_ERROR_GET_CURRENT_PATH_REQUEST_FAILED,
        }),
        severity: ToastSeverity.ERROR,
        keepOpen: true,
      };

      GzToastNotificationWebComponent.buildToastWrapperElement(toastNotificationConfigs, undefined, false);
      return null;
    }
  }

  async uploadFiles(uploadComponent: UploadWindowWebComponent) {
    const { files, streamId, path } = uploadComponent;

    const dialogConfirmButton = this.view.dialog?.shadowRoot?.querySelector('#confirm') as GzButtonWebComponent;
    const translations = this.view.getTranslations();

    const url = '/storage/remoteAccess';

    const form = new FormData();
    for (const file of files) {
      if (file.size === 0) {
        continue;
      }

      form.append(`${file.name}`, file, file.name);
    }

    const metadata = {
      streamId,
      path,
    };
    form.append('metadata', JSON.stringify(metadata));

    try {
      dialogConfirmButton.showSpinner = true;
      uploadComponent.setUploadPathReadonly(true);

      const response = await fetch(url, {
        method: 'POST',
        body: form,
      });

      if (response.ok) {
        await response.json();

        GzToastNotificationWebComponent.buildToastWrapperElement(
          {
            message: translateFn('Sending files to the endpoint.'),
            severity: ToastSeverity.INFORMATIVE,
          },
          undefined,
          false
        );

        return;
      }

      GzToastNotificationWebComponent.buildToastWrapperElement(
        {
          message: translations.upload.uploadFailed,
          severity: ToastSeverity.ERROR,
          keepOpen: true,
        },
        undefined,
        false
      );
    } catch (error) {
      GzToastNotificationWebComponent.buildToastWrapperElement(
        {
          message: translations.upload.uploadFailed,
          severity: ToastSeverity.ERROR,
          keepOpen: true,
        },
        undefined,
        false
      );

      console.log(error);
    } finally {
      dialogConfirmButton.showSpinner = false;
      uploadComponent.setUploadPathReadonly(false);
    }
  }

  async handlePreparingDownloadProcess(event) {
    try {
      const response = await ConsoleRequest.createRequest<any>({
        dataProviderType: DataProviderType.ENDPOINTS,
        endpoint: 'RemoteShellActivity',
        method: 'prepareDownloadingProcess',
        payload: {
          streamId: event.detail.streamId,
          path: event.detail.path,
          password: event.detail.password,
        },
      });
    } catch (e) {
      console.log(e);
      GzToastNotificationWebComponent.closeAllToasts();

      const toastNotificationConfigs: GzToast = {
        message: translateFn('Internal server error: {ERROR_CODE}. Contact Bitdefender Enterprise Support.', {
          ERROR_CODE: RemoteShellErrorCodes.INTERNAL_ERROR_DOWNLOAD_REQUEST_FAILED,
        }),
        severity: ToastSeverity.ERROR,
        keepOpen: true,
      };

      GzToastNotificationWebComponent.buildToastWrapperElement(toastNotificationConfigs, undefined, false);
    }
  }
}
